import { Loader2, Mail, MailCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { OTP_CODE_LENGTH, OtpCells } from '@/components/patterns/OtpCells';
import { getApiErrorMessage } from '@/lib/api/apiError';

import { useRequestSessionEmailOtp, useVerifySessionEmailOtp } from '../../api/useSessionEmailOtp';
import { useVerifyStep } from '../../api/useVerifyStep';
import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import { maskEmail, validateEmail } from './email.utils';

/**
 * Busy text for each of the three calls, matching MobileVerificationGate's "Sending code…" /
 * "Verifying…". The button label itself never changes — see the CTA below.
 */
const BUSY_LABEL = {
  sending: 'Sending code…',
  verifying: 'Verifying…',
  // The step submission, after the code is confirmed. Says "Verified" rather than "Saving…" because
  // by this point the verification has already succeeded — the applicant should see the outcome, not
  // a spinner label for bookkeeping they never asked about.
  saving: 'Verified',
} as const;

/**
 * The applicant's view of an Email Verification step.
 *
 * TWO STAGES ON ONE SCREEN COMPONENT: address, then code. The code cannot exist before the address
 * is submitted, so they cannot be one form — the same reason the workflow builder previews this step
 * as two screens.
 *
 * THREE CALLS, IN ORDER, and the order is the point:
 *   1. request  — we email a code (Studio's own SMTP; no verification provider is involved)
 *   2. verify   — the code is checked and the challenge is marked verified SERVER-SIDE
 *   3. submit   — the step is completed through the SAME `useVerifyStep` path every other step uses,
 *                 and the server re-reads the challenge rather than trusting anything sent here
 *
 * Step 3 looks redundant from the client's side and is not: it is what gives this step the attempt
 * counting, notify-on-failure, status roll-up and Return Data filtering that live in one place for
 * every step. Completing the step inside `verify` would have been a second, thinner copy of all of
 * that.
 */
export function EmailStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [expired, setExpired] = useState(false);
  /**
   * Whether the applicant has left the address field once.
   *
   * The validation message waits for this. Continue is disabled from the start, but complaining
   * "enter the part before the @" while someone is three characters into typing their address is
   * scolding them for not having finished — the message appears once they move on, and updates live
   * from then on.
   */
  const [emailTouched, setEmailTouched] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const requestOtp = useRequestSessionEmailOtp(session.id, step.id);
  const verifyOtp = useVerifySessionEmailOtp(session.id, step.id);
  const submitStep = useVerifyStep(session.id);

  const busy = requestOtp.isPending || verifyOtp.isPending || submitStep.isPending;

  /**
   * Which call is in flight, so the button's busy text says what is actually happening.
   *
   * Ordered by the sequence the three calls run in, so the label tracks progress rather than
   * whichever check happens to be written first.
   */
  const busyStage = requestOtp.isPending ? 'sending' : verifyOtp.isPending ? 'verifying' : 'saving';

  /**
   * Why the address is not acceptable, or null when it is. Recomputed every keystroke.
   *
   * This drives BOTH the disabled Continue and the inline message, so the button and the explanation
   * can never disagree — a Continue that looks pressable while the message says the address is wrong
   * is the version of this that wastes a round trip.
   */
  const emailError = validateEmail(email);

  /** Continue is dead until there is something real to send to / check. */
  const canContinue = stage === 'email' ? emailError === null : code.length === OTP_CODE_LENGTH;

  /** What to show under the address field: the server's complaint if any, else the format hint. */
  const shownEmailError = error ?? (emailTouched ? emailError : null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  function startCountdown(seconds: number) {
    setCountdown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1 && timerRef.current) clearInterval(timerRef.current);
        return c - 1;
      });
    }, 1000);
  }

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  function sendCode() {
    // Still guarded even though Continue is disabled: Enter in the field submits the form, and this
    // is also the resend path. Marks the field touched so the reason becomes visible rather than the
    // press silently doing nothing.
    if (emailError) {
      setEmailTouched(true);
      return;
    }
    setError(null);

    requestOtp.mutate(email.trim(), {
      onSuccess: (sent) => {
        setStage('code');
        setCode('');
        setMaskedEmail(sent.maskedEmail);
        // The cool-down comes FROM THE SERVER rather than being hardcoded here, so the timer can
        // never promise a resend the server will refuse.
        startCountdown(sent.resendCooldownSeconds);
      },
      onError: (err) => {
        if (isSessionExpiredError(err)) {
          setExpired(true);
          return;
        }
        // Our own wording throughout: these are OUR error codes (invalid address, cool-down, SMTP
        // failure), never a provider's or SMTP's raw text.
        setError(getApiErrorMessage(err));
      },
    });
  }

  /**
   * `override` is the freshly-typed code handed over by the OTP cells. It arrives before the state
   * update that produced it has rendered, so reading `code` here would see five digits and reject a
   * valid entry.
   */
  function submitCode(override?: string) {
    const entered = override ?? code;
    if (entered.length !== OTP_CODE_LENGTH) {
      setError(`Enter the ${OTP_CODE_LENGTH}-digit code.`);
      return;
    }
    setError(null);

    verifyOtp.mutate(entered, {
      onSuccess: () => {
        // Verified — now complete the step. The body carries the address only as a record of what
        // the applicant typed; the server reads the confirmed address off the challenge.
        submitStep.mutate(
          { stepId: step.id, input: { email: email.trim() } },
          {
            onError: (err) => {
              if (isSessionExpiredError(err)) {
                setExpired(true);
                return;
              }
              setError(getApiErrorMessage(err));
            },
          },
        );
      },
      onError: (err) => {
        if (isSessionExpiredError(err)) {
          setExpired(true);
          return;
        }
        setError(getApiErrorMessage(err));
        // Cleared so the next attempt starts from empty boxes rather than the applicant editing a
        // code already known to be wrong.
        setCode('');
      },
    });
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col">
        <div className="mb-6 text-center">
          <span
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
            style={{ backgroundColor: '#EEF3FF', color: 'var(--color-primary)' }}
          >
            {stage === 'email' ? (
              <Mail size={26} strokeWidth={1.8} />
            ) : (
              <MailCheck size={26} strokeWidth={1.8} />
            )}
          </span>
          <h2
            className="m-0 text-[20px] font-semibold leading-tight"
            style={{ color: 'var(--brand-heading, #041A3A)' }}
          >
            {stage === 'email' ? 'Email Verification' : 'Enter the code'}
          </h2>
          <p
            className="m-0 mt-1.5 text-[length:var(--text-14)] leading-snug"
            style={{ color: 'var(--brand-body, #666666)' }}
          >
            {stage === 'email'
              ? 'Enter your email address and we’ll send you a 6-digit code'
              : `We sent a 6-digit code to ${maskedEmail || maskEmail(email.trim())}`}
          </p>
        </div>

        {stage === 'email' ? (
          <form
            className="flex flex-col gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              sendCode();
            }}
          >
            <label
              htmlFor="applicant-email"
              className="block text-[length:var(--text-14)] font-medium"
              style={{ color: 'var(--brand-input-label, #000000)' }}
            >
              Email Address<span className="ml-0.5 text-red-500">*</span>
            </label>
            <input
              id="applicant-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              // The server's ceiling, so a paste that could never be accepted is stopped here rather
              // than after a round trip.
              maxLength={254}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Clears the SERVER's message only. The local one is derived from the value, so it
                // updates itself.
                setError(null);
              }}
              onBlur={() => setEmailTouched(true)}
              aria-invalid={shownEmailError ? true : undefined}
              style={{
                backgroundColor: 'var(--brand-input-fill, #ffffff)',
                borderColor: shownEmailError ? '#EF4444' : 'var(--brand-input-stroke, #D1D5DB)',
                borderRadius: 'var(--brand-input-radius, 0.5rem)',
              }}
              className="w-full border px-3.5 py-2.5 text-[14px] text-[#111827] outline-none placeholder:text-[#9CA3AF]"
            />
            {/* The SERVER's message wins when there is one — it reports something the local check
                cannot know (the address was refused, the cool-down is running, delivery failed), so
                showing the format hint over it would replace the real reason with a guess. */}
            {shownEmailError && <span className="text-xs text-destructive">{shownEmailError}</span>}
            {/* Submits the form, so Enter works as well as the button below — and only when the
                address passes, matching the disabled Continue. */}
            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
          </form>
        ) : (
          <div className="flex flex-col gap-1.5">
            <OtpCells
              value={code}
              onChange={(next) => {
                setCode(next);
                setError(null);
              }}
              onComplete={submitCode}
              disabled={busy}
            />

            {/* Right-aligned to the BOXES, which are capped at 320px and centred — aligning to the
                full-width column would park the timer at the card's far edge, disconnected from the
                row it counts for. */}
            {countdown > 0 && (
              <span className="mx-auto block w-full max-w-[320px] pr-0.5 text-right text-[12px] font-medium leading-none text-[#111827]">
                {`00:${String(countdown).padStart(2, '0')}`}
              </span>
            )}

            {error && <span className="text-center text-xs text-destructive">{error}</span>}

            {/* Resend appears only once the countdown finishes: while it runs the timer above
                already says how long is left, and showing both said the same thing twice. */}
            {countdown === 0 && (
              <span className="mt-3 text-center text-[length:var(--text-13)] text-gray66">
                Didn’t get it?{' '}
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={busy}
                  className="font-medium text-primary hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-4">
        <button
          type="button"
          onClick={() => (stage === 'email' ? sendCode() : submitCode())}
          // Disabled until the address is genuinely valid (or the code is complete), so a malformed
          // address can never reach the server and the applicant is not left guessing why their
          // press did nothing.
          disabled={busy || !canContinue}
          style={{
            backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
            color: 'var(--brand-btn-text, #ffffff)',
            borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
            borderRadius: 'var(--brand-btn-radius, 0.75rem)',
          }}
          className="flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy && <Loader2 size={15} className="animate-spin" />}
          {/* ALWAYS "Continue" — never "Send Code" / "Verify".
              Every step runner in the run says Continue, and MobileVerificationGate — which is this
              exact two-stage shape (identifier, then code) — says Continue on BOTH of its stages
              too. Naming the action per stage was a third convention for no gain: the screen visibly
              changes from a field to six boxes, so nothing is ambiguous about what the press did.
              The specificity belongs in the BUSY text below, which is where the gate puts it. */}
          {busy ? BUSY_LABEL[busyStage] : 'Continue'}
        </button>

        {stage === 'code' && (
          <button
            type="button"
            onClick={() => {
              setStage('email');
              setCode('');
              setError(null);
            }}
            disabled={busy}
            className="text-center text-sm font-medium text-primary hover:underline disabled:opacity-50"
          >
            Use a different email address
          </button>
        )}
      </div>
    </div>
  );
}
